import React, { useState, useEffect } from "react";
import { Offcanvas, Spinner } from "react-bootstrap";
import { getGalleryViewApi } from "./gallery-api.js";
import greenPersonIcon from "../../assets/svg/green_person.svg";
import bluePersonIcon from "../../assets/svg/blue_person.svg";
import truckStrokeIcon from "../../assets/svg/truck_stroke.svg";
import locationStrokeIcon from "../../assets/svg/location_stroke.svg";
import productionStrokeIcon from "../../assets/svg/production_stroke.svg";
import truckRedIcon from "../../assets/svg/truck_red.svg";
import "./gallery-view.css";

const GalleryView = ({ show, onHide, referenceId }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (show && referenceId) {
      fetchViewData(referenceId);
    } else {
      setData(null);
      setSelectedImage(null);
    }
  }, [show, referenceId]);

  const fetchViewData = async (id) => {
    setLoading(true);
    try {
      const response = await getGalleryViewApi(id);
      if (response && response.data && response.data.success) {
        const itemData = response.data.data;
        setData(itemData);
        if (itemData && itemData.attachments && itemData.attachments.length > 0 && itemData.attachments[0].file_url) {
          setSelectedImage(itemData.attachments[0].file_url);
        } else {
          setSelectedImage(null);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const currentData = data || {};
  const vehicleNumber = currentData.vehicle_number || "-";
  const refNumber = currentData.reference_no || "-";
  const activityType = currentData.activity_type === "loading" || currentData.dock_type === "load_in" ? "Loading" : "Unloading";
  const dockName = currentData.display_name || currentData.dock_number || "-";

  // Attachments
  const attachments = currentData.attachments || [];
  const mainImage = attachments.length > 0 && attachments[0].file_url ? attachments[0].file_url : null;

  // Dock in/out timings
  const dockInTimeStr = currentData.dock_in_time ? currentData.dock_in_time.replace("<br>", " ") : "-";
  const dockOutTimeStr = currentData.dock_out_time ? currentData.dock_out_time.replace("<br>", " ") : "-";

  // Dock Images
  const dockInImages = currentData.dock_in_attachments || [];
  const dockOutImages = currentData.dock_out_attachments || [];
  const dockInMainImage = dockInImages.length > 0 && dockInImages[0].file_url ? dockInImages[0].file_url : null;
  const dockOutMainImage = dockOutImages.length > 0 && dockOutImages[0].file_url ? dockOutImages[0].file_url : null;

  const allocatedUsers = currentData.allocated_users || [];

  // Selected Attachment info
  const activeAttachment = attachments.find((att) => att.file_url === selectedImage) || attachments[0] || {};
  const capturedDate = activeAttachment.created_time || "-";
  const capturedBy = activeAttachment.created_by ? ` by ${activeAttachment.created_by}` : "";
  const captureInfoText = activeAttachment.created_time || activeAttachment.created_by ? `Captured on ${capturedDate}${capturedBy}` : "-";

  return (
    <Offcanvas show={show} onHide={onHide} placement="end" className="gallery-view-offcanvas">
      <div className="gallery-view-close">
        <button onClick={onHide} className="gallery-view-close-btn">
          &times;
        </button>
      </div>

      <Offcanvas.Body className="p-0 gallery-view-body">
        {loading ? (
          <div className="d-flex justify-content-center align-items-center h-100">
            <Spinner animation="border" variant="danger" />
          </div>
        ) : !data ? (
          <div className="d-flex justify-content-center align-items-center h-100 p-4 text-center text-muted">Failed to load data or no data available.</div>
        ) : (
          <>
            {/* Top Image Section */}
            <div className="gallery-view-hero">
              <div className="gallery-view-main-image-wrapper">
                {selectedImage && <img src={selectedImage} alt="Main activity" className="gallery-view-main-img" />}
                {attachments.length > 0 && <div className="gallery-view-capture-info">{captureInfoText}</div>}
              </div>
              <div className="gallery-view-thumbnails">
                {attachments.map((att, i) => (
                  <div key={i} className={`gallery-view-thumbnail-img ${selectedImage === att.file_url ? "active" : ""}`} style={{ backgroundImage: `url(${att.file_url})` }} onClick={() => setSelectedImage(att.file_url)}></div>
                ))}
              </div>
            </div>

            {/* Info Section */}
            <div className="gallery-view-info-container">
              <div className="d-flex align-items-center mb-3">
                <div className="gallery-view-vehicle-icon">
                  <img src={truckRedIcon} alt="Truck" width="24" height="24" />
                </div>
                <div>
                  <h4 className="gallery-view-vehicle-title">{vehicleNumber}</h4>
                  <div className="gallery-view-ref-row">
                    <span className="gallery-view-ref">Ref. : {refNumber}</span>
                    <span className="gallery-view-dot">•</span>
                    <span className={`gallery-view-pill ${activityType === "Loading" ? "pill-loading" : "pill-unloading"}`}>{activityType}</span>
                  </div>
                </div>
              </div>

              <div className="gallery-view-meta-row">
                <div className="gallery-view-meta-item">
                  <img src={truckStrokeIcon} alt="Vehicle Type" width="14" height="14" />
                  <span>{currentData.vehicle_type || "-"}</span>
                </div>
                <div className="gallery-view-meta-item">
                  <img src={locationStrokeIcon} alt="Location" width="14" height="14" />
                  <span>{currentData.location ? currentData.location.split("-")[1] || currentData.location : "-"}</span>
                </div>
                <div className="gallery-view-meta-item">
                  <img src={productionStrokeIcon} alt="Department" width="14" height="14" />
                  <span>{currentData.department ? currentData.department.split("-")[1] || currentData.department : "-"}</span>
                </div>
              </div>

              {/* Dock Details */}
              <div className="gallery-view-details-grid mb-3">
                <div className="gallery-view-data-card">
                  <div className="gallery-view-data-label">Dock Number</div>
                  <div className="gallery-view-data-value">{dockName}</div>
                </div>
                <div className="gallery-view-data-card">
                  <div className="gallery-view-data-label">Duration</div>
                  <div className="gallery-view-data-value">{currentData.duration || "-"}</div>
                </div>
              </div>

              <div className="gallery-view-details-grid mb-4">
                <div className="gallery-view-data-card p-0">
                  <div className="p-3 pb-0">
                    <div className="gallery-view-data-label">Dock In Time</div>
                    <div className="gallery-view-data-value-sm" dangerouslySetInnerHTML={{ __html: currentData.dock_in_time ? `${currentData.dock_in_time}<br/>by ${currentData.dock_in_user || "-"}` : "-" }}></div>
                  </div>
                  {dockInMainImage && <img src={dockInMainImage} className="gallery-view-dock-img" alt="Dock In" />}
                </div>
                <div className="gallery-view-data-card p-0">
                  <div className="p-3 pb-0">
                    <div className="gallery-view-data-label">Dock Out Time</div>
                    <div className="gallery-view-data-value-sm" dangerouslySetInnerHTML={{ __html: currentData.dock_out_time ? `${currentData.dock_out_time}<br/>by ${currentData.dock_out_user || "-"}` : "-" }}></div>
                  </div>
                  {dockOutMainImage && <img src={dockOutMainImage} className="gallery-view-dock-img" alt="Dock Out" />}
                </div>
              </div>

              {/* Persons Details */}
              <div className="gallery-view-details-grid">
                <div className="gallery-view-person-card">
                  <div className="gallery-view-person-label">ALLOCATED PERSON</div>
                  <div style={{ maxHeight: "100px", overflowY: "auto", paddingRight: "4px" }} className="custom-scrollbar">
                    {allocatedUsers.length > 0 ? (
                      allocatedUsers.map((user, idx) => (
                        <div className="d-flex align-items-center mt-2 mb-2" key={`alloc-user-${idx}`}>
                          <div className="gallery-view-avatar flex-shrink-0" style={{ backgroundColor: "#F5F7F9" }}>
                            <img src={bluePersonIcon} alt="Allocated Person" width="20" height="20" />
                          </div>
                          <div>
                            <div className="gallery-view-person-name">{user.first_name || "-"}</div>
                            <div className="gallery-view-person-sub">{user.assigned_at || "-"}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="d-flex align-items-center mt-2">
                        <div className="gallery-view-avatar flex-shrink-0" style={{ backgroundColor: "#F5F7F9" }}>
                          <img src={bluePersonIcon} alt="Allocated Person" width="20" height="20" />
                        </div>
                        <div>
                          <div className="gallery-view-person-name">-</div>
                          <div className="gallery-view-person-sub">-</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="gallery-view-person-card">
                  <div className="gallery-view-person-label">DRIVER</div>
                  <div className="d-flex align-items-center mt-2">
                    <div className="gallery-view-avatar" style={{ backgroundColor: "#F5F7F9" }}>
                      <img src={greenPersonIcon} alt="Driver" width="20" height="20" />
                    </div>
                    <div>
                      <div className="gallery-view-person-name">{currentData.driver_name || "-"}</div>
                      <div className="gallery-view-person-sub">
                        License : <span className="text-primary">{currentData.license_number || "-"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default GalleryView;
